import {
  Ban,
  BookOpen,
  Bot,
  FileStack,
  Download,
  ImageUp,
  MessagesSquare,
  PanelRight,
  Printer,
  ScanSearch,
  ShieldCheck,
  ShieldQuestion,
  Workflow,
  Zap,
  Hourglass,
} from "lucide-react";
import {
  useAronPersonaUi,
  useAronConversationInfo,
  useAronMessages,
  useAronWorkModes,
} from "../api/aronApi";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AronWordmark } from "./AronMark";
import { useAronUiStore } from "../store/aronUiStore";
import { AronYoloContext, useAronYolo } from "./aronYolo";
import { useAronConversationExport } from "../hooks/useAronConversationExport";
import { AronComposer, type AronComposerHandle } from "./AronComposer";
import { AronMessageList } from "./AronMessageList";
import { AronNotebook } from "./AronNotebook";

interface Props {
  conversationId: string | null;
  sending: boolean;
  onSend: (text: string, files?: File[], opts?: { compressImages?: boolean }) => void;
  onStop: () => void;
  /** When provided, shows a "Wydziel" header action that pops this conversation
   * into the detached right-hand dock. Omitted inside the dock itself. */
  onDetach?: () => void;
  /** Mobile only — opens the conversation-list drawer (the sidebar is hidden on
   * small screens). */
  onOpenList?: () => void;
  /** The workbench moves these actions into the tab menu, so its panes render
   * without the header row — vertical space is scarce in a split pane. */
  showHeader?: boolean;
}

/** Jeden fakt o asystencie: ikona + zdanie. Trzy takie zamiast akapitu — w
 * wąskim panelu i tak nikt nie czyta ściany tekstu. */
function Fact({
  icon: Icon,
  children,
}: {
  icon: typeof Bot;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-2">
      <Icon size={14} className="mt-0.5 shrink-0 text-gray-400" />
      <span className="text-gray-500 dark:text-gray-400">{children}</span>
    </li>
  );
}

function Examples({ items }: { items: string[] }) {
  return (
    <div className="w-full max-w-md">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-400">
        Na przykład
      </p>
      <ul className="mt-1 space-y-0.5 border-l-2 border-red-200 pl-2.5 text-left text-xs text-gray-500 dark:border-red-900/50 dark:text-gray-400">
        {items.map((t) => (
          <li key={t} className="italic">
            „{t}"
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyState() {
  const persona = useAronPersonaUi();
  const yolo = useAronYolo();
  if (persona === "pdf") {
    return (
      <div className="mx-auto flex h-full max-w-md flex-col items-center justify-center gap-4 px-6 py-8">
        <span className="inline-flex h-12 w-12 items-center justify-center bg-[var(--color-primary)] text-white">
          <FileStack size={24} />
        </span>
        <div className="text-center">
          <h2 className="font-mono text-base font-bold uppercase tracking-[0.12em]">
            Asystent PDF
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Widzi pliki tej sprawy i potrafi ich dotknąć.
          </p>
        </div>
        <ul className="w-full space-y-1.5 text-left text-sm">
          <Fact icon={ScanSearch}>
            Naprawdę ogląda strony — także skany bez tekstu. Zapytaj, co jest na
            trzeciej stronie.
          </Fact>
          <Fact icon={Workflow}>
            Używa tych samych narzędzi co kafelki obok, a gdy żaden nie pasuje —
            pisze własny skrypt na Twoich plikach.
          </Fact>
          <Fact icon={ShieldQuestion}>
            Danych firmy nie widzi, dopóki nie włączysz przełącznika „Dane z 5-tki”
            (wymaga modułu ARON).
          </Fact>
        </ul>
        <Examples
          items={[
            "wywal puste strony i scal wszystko w jeden plik",
            "zrób OCR skanu i powiedz, jakie są kwoty netto",
            "zakryj numery kont na stronie 2 i zapisz jako nowy plik",
          ]}
        />
      </div>
    );
  }
  if (persona === "eng") {
    return (
      <div className="mx-auto flex h-full max-w-md flex-col items-center justify-center gap-4 px-6 py-8">
        <span className="inline-flex h-12 w-12 items-center justify-center bg-amber-500 text-white">
          <Bot size={24} />
        </span>
        <div className="text-center">
          <h2 className="font-mono text-base font-bold uppercase tracking-[0.12em]">
            ENG-ARON
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Drugie oko na dokumentację projektową.
          </p>
        </div>
        <ul className="w-full space-y-1.5 text-left text-sm">
          <Fact icon={BookOpen}>
            Odpowiada z firmowej bazy wiedzy: wytyczne plus katalog błędów
            wyłapanych na poprzednich opracowaniach.
          </Fact>
          <Fact icon={ImageUp}>
            Wklej screen albo PDF fragmentu rysunku — obejrzy i odniesie się do
            konkretu.
          </Fact>
          <Fact icon={Ban}>
            Nie widzi danych ELG5 (zamówień, faktur, przedsięwzięć) — od tego
            jest ARON.
          </Fact>
        </ul>
        <Examples
          items={[
            "jak opisujemy zbliżenia do sieci obcej?",
            "co ustaliliśmy z tym inwestorem przy poprzednim projekcie?",
            "sprawdź ten fragment opisu technicznego",
          ]}
        />
      </div>
    );
  }
  return (
    <div className="mx-auto flex h-full max-w-md flex-col items-center justify-center gap-4 px-6 py-8">
      {/* Znak (owczarek) wraca później — na razie sama sygnatura. */}
      <span
        className={`inline-flex h-24 w-24 items-center justify-center text-white ${
          yolo ? "bg-black dark:bg-white dark:text-black" : "bg-red-600"
        }`}
      >
        <AronWordmark className="text-3xl" />
      </span>
      <div className="text-center">
        <h2 className="font-mono text-base font-bold uppercase tracking-[0.12em]">
          ARON 2.0
        </h2>
        <p className="mt-0.5 text-sm text-gray-500">
          Zna ELG5 od środka: zamówienia, faktury, POCK, przedsięwzięcia,
          przetargi.
        </p>
      </div>
      <ul className="w-full space-y-1.5 text-left text-sm">
        <Fact icon={ScanSearch}>
          Sam idzie po dane i liczy na żywym systemie — nie zgaduje z pamięci.
          Pod każdą liczbą zostawia źródło, więc masz jak sprawdzić.
        </Fact>
        <Fact icon={Workflow}>
          Bierze całą sprawę, nie jedno pytanie: przechodzi ją krok po kroku,
          zrobi plik, umie wracać do tematu cyklicznie.
        </Fact>
        <Fact icon={ShieldCheck}>
          Widzi tyle, ile Ty. Zmiany w systemie tylko proponuje — do bazy trafia
          wyłącznie to, co sam zatwierdzisz.
        </Fact>
      </ul>
      <Examples
        items={[
          "zsumuj faktury podwykonawców z tego miesiąca per przedsięwzięcie",
          "które POCK zalegają i na kim stoją?",
          "przygotuj zapotrzebowanie na MDCP i daj do zatwierdzenia",
        ]}
      />
    </div>
  );
}

export function AronChatView({
  conversationId,
  sending,
  onSend,
  onStop,
  onDetach,
  onOpenList,
  showHeader = true,
}: Props) {
  const { data: messages = [], isFetched: messagesLoaded } = useAronMessages(conversationId);
  const conv = useAronConversationInfo(conversationId);
  const persona = useAronPersonaUi();
  const autopilotDefault = useAronUiStore((s) => s.autopilotDefault);
  // Tryb YOLO — steruje całą oprawą (kolory + pasek ostrzegawczy). Rozpoczęta
  // rozmowa ma go zapisany u siebie; świeża karta bierze ustawienie z pickera,
  // inaczej ekran startowy świeciłby na czerwono pod czarnym chipem „YOLO!".
  const yolo = conversationId ? !!conv?.autopilot : autopilotDefault;
  const hasStreamingAssistant = messages.some(
    (m) =>
      m.role === "assistant" &&
      (m.status === "streaming" || m.status === "pending"),
  );
  // "ARON myśli…" only bridges the gap between dispatch and the first event.
  // Derive it from the transcript — once an assistant bubble exists (streaming OR
  // already finished as the last message) we hide it, so a stale `sending` flag can
  // never leave it spinning after the answer is in.
  const lastMsg = messages[messages.length - 1];
  const awaitingFirstToken =
    sending && !hasStreamingAssistant && (!lastMsg || lastMsg.role === "user");
  // The turn is "in flight" from dispatch until the assistant message reaches a
  // terminal status. Drive a persistent working indicator off this (not the page
  // flag) so it shows throughout long multi-tool turns and reliably hides at the end.
  const turnActive = awaitingFirstToken || hasStreamingAssistant;
  // Na co rozmowa CZEKA w tle. Dopóki coś wisi, odświeżamy dane rozmowy co 30 s —
  // beat rozlicza znaczniki co minutę, a człowiek ma widzieć moment, w którym
  // „czekamy na kompilację" zmienia się w kolejną turę, nie odświeżać ręcznie.
  const waits = conv?.background_waits ?? [];
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!conversationId || waits.length === 0) return;
    const t = window.setInterval(
      () => queryClient.invalidateQueries({ queryKey: ["aron"] }),
      30_000,
    );
    return () => window.clearInterval(t);
  }, [conversationId, waits.length, queryClient]);
  // Po odpowiedzi pracuje jeszcze RECENZENT — użytkownik musi widzieć, że to już nie
  // ARON pisze, tylko niezależna ocena (zgłoszenie właściciela 2026-08-23: „nie
  // pokazuje się wprost, że to recenzent teraz pracuje, a nie ARON").
  const recenzentPracuje =
    lastMsg?.role === "assistant" && lastMsg.gate?.phase === "start" && lastMsg.gate?.score == null;
  const busy = turnActive;
  // Dopisek w trakcie odpowiedzi (jak korekta wpisana w Claude Code w połowie tury):
  // tylko zwykły czat (YOLO wykonuje zapisy w jednej transakcji — tam zostaje Stop) i
  // tylko model, o którym backend mówi, że przyjmie tekst po wynikach narzędzi.
  const { data: workModes } = useAronWorkModes();
  const modeSupportsAddendum = !!workModes?.reasoning_modes.find(
    (m) => m.value === conv?.reasoning_mode,
  )?.addendum;
  const addendumAllowed = busy && !yolo && modeSupportsAddendum;

  const { exporting, downloadLog, printConversation } =
    useAronConversationExport(conversationId);

  // „Trzymaj się dna". Widok dojeżdża do najnowszej treści przy KAŻDEJ zmianie
  // wysokości — nowe tokeny, zwinięty blok rozumowania, znikający pasek „ARON
  // pracuje" — bo samo dołożenie wiadomości to za mało: gdy blok myślenia się
  // zamyka, strona kurczy się bez żadnego zdarzenia i widok zostaje w górze.
  // Warunek jest jeden: czytelnik stoi na dole. Odjechał wyżej, żeby coś
  // przeczytać — nie ruszamy go, dopóki sam nie wróci na dół.
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  useEffect(() => {
    const box = scrollRef.current;
    const content = contentRef.current;
    // jsdom (i bardzo stare przeglądarki) nie znają ResizeObserver — bez niego
    // zostaje zwykłe przewijanie ręczne, zamiast wywrotki całego widoku.
    if (!box || !content || typeof ResizeObserver === "undefined") return;
    const toBottom = () => {
      if (stick.current) box.scrollTop = box.scrollHeight;
    };
    const ro = new ResizeObserver(toBottom);
    ro.observe(content);
    return () => ro.disconnect();
  }, []);

  // Inny wątek = inna rozmowa: zaczynamy od najnowszej wiadomości i od nowa
  // przyklejeni do dna.
  useEffect(() => {
    const box = scrollRef.current;
    stick.current = true;
    if (box) box.scrollTop = box.scrollHeight;
  }, [conversationId]);

  // Przeciągnięcie pliku nad rozmowę. Bez tego upuszczenie kończyło się na
  // globalnym `preventDefault` z main.tsx (blokada nawigacji do pliku) i po
  // prostu nic się nie działo — plik dało się dołączyć wyłącznie spinaczem.
  // Zdarzenia bez plików (przeciąganie karty warsztatu, zaznaczonego tekstu)
  // przepuszczamy nietknięte, żeby nie zabrać ich własnym strefom upuszczania.
  const composerRef = useRef<AronComposerHandle>(null);
  const [dropActive, setDropActive] = useState(false);
  // dragenter/dragleave sypią się przy każdym przejściu nad dzieckiem — licznik
  // zamiast flagi, inaczej podświetlenie mruga nad każdym dymkiem.
  const dragDepth = useRef(0);

  const hasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer.types).includes("Files");

  const onDragEnter = (e: React.DragEvent) => {
    if (!hasFiles(e) || !composerRef.current?.attachAllowed) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDropActive(true);
  };

  const onDragOver = (e: React.DragEvent) => {
    if (!hasFiles(e) || !composerRef.current?.attachAllowed) return;
    // Bez tego przeglądarka nie uzna elementu za cel i nie wyśle „drop".
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDropActive(false);
  };

  const onDropFiles = (e: React.DragEvent) => {
    if (!hasFiles(e) || !composerRef.current?.attachAllowed) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDropActive(false);
    // Katalogi trafiają tu jako wpisy o zerowym rozmiarze i bez typu — dołożony
    // „plik", którego backend i tak nie przyjmie, myli bardziej niż jego brak.
    const dropped = Array.from(e.dataTransfer.files).filter((f) => f.size > 0);
    composerRef.current.addFiles(dropped);
  };

  return (
    <AronYoloContext.Provider value={yolo}>
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDropFiles}
      className={`relative flex h-full flex-col bg-white dark:bg-gray-900 ${
        yolo ? "ring-2 ring-inset ring-black dark:ring-white" : ""
      }`}
    >
      {dropActive && (
        <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center bg-white/85 dark:bg-gray-900/85">
          <div className="flex items-center gap-2 border-2 border-dashed border-red-400 px-4 py-3 text-sm text-red-600 dark:text-red-400">
            <ImageUp size={16} /> Upuść pliki, aby dołączyć do wiadomości
          </div>
        </div>
      )}
      {yolo && (
        <div className="flex shrink-0 items-center gap-1.5 bg-black px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-white">
          <Zap size={11} /> YOLO! — ARON zapisuje od razu, bez kart do
          zatwierdzenia
        </div>
      )}
      {showHeader && (onDetach || onOpenList || conversationId) && (
        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-3 py-1.5 dark:border-gray-700 dark:bg-gray-900">
          {/* Mobile only — sidebar list is hidden on small screens. */}
          {onOpenList ? (
            <button
              type="button"
              onClick={onOpenList}
              title="Pokaż listę rozmów"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600 md:hidden dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <MessagesSquare size={14} /> Rozmowy
            </button>
          ) : (
            <span />
          )}
          <div className="ml-auto flex items-center gap-1">
            {conversationId && (
              <button
                type="button"
                onClick={printConversation}
                disabled={messages.length === 0}
                title="Wydrukuj rozmowę lub zapisz jako PDF (w oknie drukowania wybierz „Zapisz jako PDF”)"
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                <Printer size={14} /> Drukuj / PDF
              </button>
            )}
            {conversationId && (
              <button
                type="button"
                onClick={downloadLog}
                disabled={exporting}
                title="Pobierz pełny zapis rozmowy (logi request-response każdego zapytania) do diagnozy"
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                <Download size={14} />{" "}
                {exporting ? "Pobieram…" : "Pobierz zapis"}
              </button>
            )}
            {onDetach ? (
              <button
                type="button"
                onClick={onDetach}
                title="Wydziel rozmowę do panelu po prawej — zobacz inną stronę obok"
                className="hidden items-center gap-1.5 rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-red-600 md:inline-flex dark:text-gray-400 dark:hover:bg-gray-800"
              >
                <PanelRight size={14} /> Wydziel do panelu
              </button>
            ) : (
              <span />
            )}
          </div>
        </div>
      )}
      {/* data-aron-scroll: the workbench keeps background tabs mounted but
          hidden, and re-pins them to the newest message when they come back. */}
      <div
        data-aron-scroll
        ref={scrollRef}
        onScroll={(e) => {
          // Ostatnie ~40 px liczy się jako „dno" — poniżej tego progu użytkownik
          // czyta coś wyżej i autoprzewijanie ma się nie odzywać.
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
        className="flex-1 overflow-y-auto"
      >
        <div ref={contentRef}>
        {/* Pusty wątek pokazuje wizytówkę asystenta — nie tylko „jeszcze nie
            założona rozmowa". Moduł PDF zakłada wątek z góry (przypina go do
            sprawy), więc bez tego witałby użytkownika białą płachtą. Warunek
            `messagesLoaded` chroni przed mignięciem wizytówki, zanim dojedzie
            historia istniejącej rozmowy. */}
        {messages.length === 0 && !sending && (!conversationId || messagesLoaded) ? (
          <EmptyState />
        ) : (
          <>
            <AronMessageList
              messages={messages}
              conversationId={conversationId}
            />
            {recenzentPracuje && !turnActive && (
              <div className="mx-auto flex w-full max-w-4xl items-center gap-2 px-4 pb-5 text-sm text-amber-700 dark:text-amber-400">
                <ShieldQuestion size={14} className="animate-pulse" />
                Recenzent sprawdza odpowiedź…
              </div>
            )}
            {turnActive && (
              <div className="mx-auto flex w-full max-w-4xl items-center gap-2 px-4 pb-5 text-sm text-gray-400">
                <span className="inline-flex gap-1" aria-hidden>
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      style={{ animationDelay: `${-0.3 + i * 0.15}s` }}
                      className={`h-1.5 w-1.5 animate-bounce ${
                        yolo ? "bg-black dark:bg-white" : "bg-red-500"
                      }`}
                    />
                  ))}
                </span>
                {recenzentPracuje
                  ? "Recenzent sprawdza odpowiedź…"
                  : awaitingFirstToken
                    ? "ARON myśli…"
                    : "ARON pracuje…"}
              </div>
            )}
            {/* „Zleciłem, wracam" — dopóki znacznik powrotu wisi, człowiek widzi NA CO
                i OD KIEDY czeka. Gdy ARON napisał „wrócę", a tu nic nie ma, to też
                jest informacja: nikt sam nie wróci (EOP 83, 04.09.2026). */}
            {!turnActive && waits.length > 0 && (
              <div className="mx-auto w-full max-w-4xl px-4 pb-5 text-sm">
                <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                  <Hourglass size={14} className="mt-0.5 shrink-0 animate-pulse" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">
                      Czekam na {waits.length === 1 ? "zadanie w tle" : `${waits.length} zadania w tle`} —
                      ARON wróci sam, gdy się skończy
                    </div>
                    <ul className="mt-1 space-y-0.5 text-[12px]">
                      {waits.map((w) => (
                        <li key={w.task_id} className="flex flex-wrap gap-x-2 tabular-nums">
                          <span className="truncate">{w.label || "zadanie w tle"}</span>
                          <span className="text-amber-700/80 dark:text-amber-400/80">
                            zlecono {new Date(w.since).toLocaleTimeString("pl-PL")}
                            {w.deadline
                              ? ` · najpóźniej do ${new Date(w.deadline).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`
                              : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </div>
      {/* Na czym ARON opiera decyzje w tej rozmowie — nad polem wiadomości, żeby
          pomyłkę (obce przedsięwzięcie, zła umowa) dało się złapać ZANIM zdąży coś
          na jej podstawie zapisać. Persona PDF/ENG notatnika nie ma. */}
      {persona === "aron" && (
        <AronNotebook conversationId={conversationId} busy={busy} />
      )}
      <AronComposer
        ref={composerRef}
        onSend={onSend}
        busy={busy}
        onStop={onStop}
        addendumAllowed={addendumAllowed}
        conversationId={conversationId}
      />
    </div>
    </AronYoloContext.Provider>
  );
}
